import React, { FC, useMemo } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { scale } from '@components/frame';
import { ChangeType, ProcessedLine, GroupPosition } from './types';
import { font } from '@styles/fonts';
import { useManageDiffLine } from './hooks/useManageDiff';
import { WithColor } from '@styles/colors';

interface DiffLineProps extends WithColor{
  line: ProcessedLine;
  isOldVersion: boolean;
  groupPosition?: GroupPosition;
  isFirstLineInDocument?: boolean;
}

const DiffLine: FC<DiffLineProps> = ({ C, line, isOldVersion, groupPosition, isFirstLineInDocument }) => {
  const { isTablet, containerStyle, placeholderRowStyle, firstLineTabletPadding, lineNumber, content, inlineSegments } = useManageDiffLine({
    C,
    line,
    isOldVersion,
    groupPosition,
    isFirstLineInDocument,
  });

  const tabletRowHeight = useMemo(() => Math.round(scale(26, false)), []);

  const renderContent = () => {
    if (inlineSegments.hasSegments) {
      return (
        <Text style={[font['NC Diff Mono'], styles.content, isTablet && styles.tabletContent, { color: content.color, opacity: content.opacity }]}>
          {inlineSegments.segments.map((segment) => (
            <Text
              key={`${line.key}-${segment.id}`}
              style={[
                styles.inlineSegment,
                segment.backgroundColor !== 'transparent' && { backgroundColor: segment.backgroundColor },
                { color: segment.textColor },
              ]}
            >
              {segment.value || ' '}
            </Text>
          ))}
        </Text>
      );
    }

    if (content.isPlaceholderLine) {
      return (
        <Text style={[font['NC Diff Mono'], styles.content, isTablet && styles.tabletContent, { color: 'transparent' }]}>
          {content.placeholderGlyph}
        </Text>
      );
    }

    return (
      <Text style={[font['NC Diff Mono'], styles.content, isTablet && styles.tabletContent, { color: content.color, opacity: content.opacity }]}>
        {content.value || (content.isPlaceholderValue ? content.placeholderGlyph : '')}
      </Text>
    );
  };

  const contentNode = renderContent();

  return (
    <View
      style={[
        styles.container,
        isTablet && styles.tabletContainer,
        isTablet && { minHeight: tabletRowHeight },
        placeholderRowStyle,
        firstLineTabletPadding,
        containerStyle,
        { flex: 1, marginHorizontal: scale(9) }, // Ensure 9px inset for semantic coloring
      ]}
    >
      {/* Line numbers temporarily disabled - NC files have embedded line numbers */}
      {/* <View style={[styles.lineNumberContainer, isTablet && [styles.lineNumberContainerTablet, { height: tabletRowHeight }]]}>
        {lineNumber.display && (
          <Text
            style={[
              font['NC Diff Mono'],
              styles.lineNumberText,
              isTablet && styles.tabletLineNumberText,
              { color: lineNumber.color },
              lineNumber.hidden && styles.hiddenLineNumber,
            ]}
          >
            {lineNumber.display}
          </Text>
        )}
      </View> */}

      <View style={[styles.contentContainer, isTablet && [styles.contentContainerTablet, { height: tabletRowHeight }]]}>{contentNode}</View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: scale(6),
    minHeight: scale(26),
  },
  tabletContainer: {
    paddingVertical: scale(0),
  },
  lineNumberContainer: {
    alignItems: 'flex-end',
  },
  lineNumberContainerTablet: {
    justifyContent: 'center',
  },
  lineNumberText: {
    fontSize: scale(16),
    lineHeight: scale(20),
    marginHorizontal: scale(12),
  },
  tabletLineNumberText: {
    lineHeight: scale(18),
  },
  hiddenLineNumber: {
    color: 'transparent',
    opacity: 0,
  },
  contentContainer: {
    flex: 1,
    paddingHorizontal: scale(9),
  },
  contentContainerTablet: {
    justifyContent: 'center',
  },
  content: {
    fontSize: scale(16),
    lineHeight: scale(22),
  },
  tabletContent: {
    lineHeight: scale(18),
    paddingTop: scale(4, false),
  },
  inlineSegment: {
    borderRadius: scale(4),
    paddingHorizontal: scale(2),
  },
});

export default DiffLine;
